import { useCallback, useEffect, useRef, useState } from 'react';
import { FACE_SHAPES, SHAPE_ORDER } from './data/shapeContent';
import { releasePreparedPhoto, type PreparedPhoto } from './lib/imageUtils';
import { disposeFaceLandmarker } from './lib/vision';
import type { FaceAnalysis } from './types';
import { AnalyzerTabs, type AnalyzerMode } from './components/AnalyzerTabs';
import {
  ArrowIcon,
  CheckIcon,
  CloseIcon,
  InfoIcon,
  LockIcon,
  MenuIcon,
  RulerIcon,
  SparkIcon,
} from './components/Icons';
import { LegalDialog } from './components/LegalDialog';
import { ManualAnalyzer } from './components/ManualAnalyzer';
import { PhotoAnalyzer } from './components/PhotoAnalyzer';
import { ResultView } from './components/ResultView';
import { ShapeGlyph } from './components/ShapeGlyph';

const QUESTIONS = [
  {
    question: 'Can a face be a mix of two shapes?',
    answer:
      'Yes. Face-shape names simplify continuous human variation. When the first two geometry profiles are close, the tool calls the result blended and shows both instead of forcing false certainty.',
  },
  {
    question: 'How accurate is a photo face-shape detector?',
    answer:
      'A front-facing, well-lit photo can provide a useful styling estimate, but camera angle, expression, hair coverage, lens distortion, and the chosen classification method all affect the result. This tool publishes its ratios and close alternatives rather than claiming a universal accuracy percentage.',
  },
  {
    question: 'What photo gives the best measurement?',
    answer:
      'Use one face, look straight at the camera, keep your head level, relax your expression, pull hair away from the outline, and leave the full forehead, cheeks, jaw, and chin visible. Avoid close wide-angle selfies when possible.',
  },
  {
    question: 'Does the site save or upload my face?',
    answer:
      'No analysis upload is built into the site. The photo is decoded, landmarked, and classified in this browser. The model and WebAssembly files come from the same site origin, and the browser security policy blocks third-party runtime connections.',
  },
  {
    question: 'Why might the manual and photo results differ?',
    answer:
      'A photo measures the visible two-dimensional contour, while a tape measurement depends on where each point is placed. Perspective and jaw-description choices also matter. Treat a disagreement as a sign that the result is near a boundary, not that one label must be absolute.',
  },
] as const;

function App(): React.ReactElement {
  const [mode, setMode] = useState<AnalyzerMode>('photo');
  const [analysis, setAnalysis] = useState<FaceAnalysis | null>(null);
  const [photo, setPhoto] = useState<PreparedPhoto | null>(null);
  const [legalOpen, setLegalOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [photoSession, setPhotoSession] = useState(0);
  const photoRef = useRef<PreparedPhoto | null>(null);

  const replacePhoto = useCallback((nextPhoto: PreparedPhoto | null) => {
    setPhoto((currentPhoto) => {
      if (currentPhoto && currentPhoto !== nextPhoto) releasePreparedPhoto(currentPhoto);
      return nextPhoto;
    });
  }, []);

  const resetAnalysis = useCallback(() => {
    setAnalysis(null);
    replacePhoto(null);
    setPhotoSession((current) => current + 1);
  }, [replacePhoto]);

  const changeMode = useCallback(
    (nextMode: AnalyzerMode) => {
      if (nextMode === mode) return;
      resetAnalysis();
      setMode(nextMode);
    },
    [mode, resetAnalysis],
  );

  useEffect(() => {
    photoRef.current = photo;
  }, [photo]);

  useEffect(() => {
    return () => {
      releasePreparedPhoto(photoRef.current);
      void disposeFaceLandmarker();
    };
  }, []);

  return (
    <>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="What's My Face Shape home">
          <img src="/favicon.svg" width="40" height="40" alt="" />
          <span>
            <strong>What’s My</strong>
            <small>Face Shape</small>
          </span>
        </a>

        <button
          className="menu-button"
          type="button"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          aria-controls="site-navigation"
          onClick={() => setNavOpen((current) => !current)}
        >
          {navOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        <nav id="site-navigation" className="site-nav" data-open={navOpen ? 'true' : 'false'} aria-label="Primary navigation">
          <a href="#tool" onClick={() => setNavOpen(false)}>
            Analyzer
          </a>
          <a href="#shapes" onClick={() => setNavOpen(false)}>
            7 shapes
          </a>
          <a href="#method" onClick={() => setNavOpen(false)}>
            Method
          </a>
          <button
            type="button"
            onClick={() => {
              setNavOpen(false);
              setLegalOpen(true);
            }}
          >
            Terms & Privacy
          </button>
        </nav>
      </header>

      <main id="top">
        <section id="tool" className="hero-section" aria-labelledby="hero-heading">
          <div className="hero-copy">
            <span className="eyebrow">
              <SparkIcon /> Private face geometry tool
            </span>
            <h1 id="hero-heading">What is my face shape?</h1>
            <p className="hero-copy__lede">
              See which of seven face-shape profiles best matches your visible proportions—plus the close alternatives and the measurements behind the result.
            </p>

            <div className="hero-proof" aria-label="Product promises">
              <div>
                <CheckIcon />
                <span>
                  <strong>Free, no account</strong>
                  Photo scan and manual calculator
                </span>
              </div>
              <div>
                <LockIcon />
                <span>
                  <strong>No photo upload</strong>
                  Analysis stays inside this tab
                </span>
              </div>
              <div>
                <RulerIcon />
                <span>
                  <strong>Explainable result</strong>
                  Ratios, overlay, and close matches
                </span>
              </div>
            </div>

            <aside className="hero-privacy" aria-label="Privacy architecture summary">
              <span className="hero-privacy__icon">
                <LockIcon />
              </span>
              <div>
                <strong>A privacy promise the browser can enforce.</strong>
                <p>
                  The model and analyzer load from this same site. A strict network policy prevents the page from sending your portrait to an analysis service.
                </p>
              </div>
              <button type="button" className="text-button" onClick={() => setLegalOpen(true)}>
                Read how it works <ArrowIcon />
              </button>
            </aside>
          </div>

          <div className="tool-column">
            <div className="tool-column__topline">
              <span>Choose one method</span>
              <span>Typical task: under a minute</span>
            </div>
            <AnalyzerTabs mode={mode} onChange={changeMode} />
            {mode === 'photo' ? (
              <PhotoAnalyzer
                key={`photo-session-${photoSession}`}
                photo={photo}
                analysis={analysis?.source === 'photo' ? analysis : null}
                onPhotoChange={replacePhoto}
                onAnalysisChange={setAnalysis}
                onUseManual={() => changeMode('manual')}
              />
            ) : (
              <ManualAnalyzer
                onAnalysisChange={(nextAnalysis) => {
                  replacePhoto(null);
                  setAnalysis(nextAnalysis);
                }}
              />
            )}
            {analysis ? <ResultView analysis={analysis} photo={photo} onReset={resetAnalysis} /> : null}
          </div>
        </section>

        <section id="shapes" className="shape-library section-shell" aria-labelledby="shapes-heading">
          <div className="section-intro section-intro--split">
            <div>
              <span className="eyebrow">The seven comparison profiles</span>
              <h2 id="shapes-heading">A shape is a shorthand, not a box.</h2>
            </div>
            <p>
              Real faces vary continuously. These profiles are useful because they describe the relationship between length, forehead, cheekbones, jaw, and taper—not because every person fits a perfect outline.
            </p>
          </div>

          <div className="shape-index">
            {SHAPE_ORDER.map((shape, index) => {
              const content = FACE_SHAPES[shape];
              return (
                <article className="shape-index__item" key={shape}>
                  <span className="shape-index__number">0{index + 1}</span>
                  <ShapeGlyph shape={shape} />
                  <h3>{content.label}</h3>
                  <p>{content.signature}</p>
                  <small>
                    Often close to {content.oftenConfusedWith.map((id) => FACE_SHAPES[id].label).join(' or ')}
                  </small>
                </article>
              );
            })}
          </div>
        </section>

        <section id="method" className="method-section section-shell" aria-labelledby="method-heading">
          <div className="section-intro">
            <span className="eyebrow">Transparent by design</span>
            <h2 id="method-heading">From portrait to result, without a black box.</h2>
            <p>
              The tool turns a visible face outline into ratios, then compares those ratios with seven calibrated geometry profiles. The leading profile becomes the label; the next profiles remain visible so borderline cases are not hidden.
            </p>
          </div>

          <div className="method-steps">
            <article>
              <span>01</span>
              <div>
                <h3>Map the visible outline</h3>
                <p>
                  The local face-landmark model finds the forehead, cheeks, jaw, chin, and eye line. The eye line helps correct minor head tilt before measuring.
                </p>
              </div>
            </article>
            <article>
              <span>02</span>
              <div>
                <h3>Measure four proportions</h3>
                <p>
                  Face length, forehead width, cheekbone width, and jaw width are normalized against the widest point. Jaw taper and corner character help separate similar shapes.
                </p>
              </div>
            </article>
            <article>
              <span>03</span>
              <div>
                <h3>Compare, then show uncertainty</h3>
                <p>
                  A weighted distance ranks all seven profiles. Close scores produce a blended result; no bar is presented as a medical probability or universal accuracy rate.
                </p>
              </div>
            </article>
          </div>

          <div className="formula-strip" aria-label="Face shape measurement formula">
            <div>
              <span>Length ratio</span>
              <strong>face length ÷ widest width</strong>
            </div>
            <span aria-hidden="true">+</span>
            <div>
              <span>Width pattern</span>
              <strong>forehead ↔ cheeks ↔ jaw</strong>
            </div>
            <span aria-hidden="true">+</span>
            <div>
              <span>Lower contour</span>
              <strong>corner angle + chin taper</strong>
            </div>
          </div>
        </section>

        <section className="photo-guide section-shell" aria-labelledby="photo-guide-heading">
          <div className="photo-guide__visual" aria-hidden="true">
            <div className="photo-guide__frame">
              <ShapeGlyph shape="oval" measured />
              <span className="photo-guide__line photo-guide__line--horizontal" />
              <span className="photo-guide__line photo-guide__line--vertical" />
            </div>
          </div>
          <div className="photo-guide__copy">
            <span className="eyebrow">Better input, steadier comparison</span>
            <h2 id="photo-guide-heading">Give the outline room to be measured.</h2>
            <ul className="check-list">
              <li>
                <CheckIcon /> Face the camera directly and keep the eye line level.
              </li>
              <li>
                <CheckIcon /> Use even front lighting; avoid a bright window behind you.
              </li>
              <li>
                <CheckIcon /> Move hair, hats, hands, and large frames away from the outline.
              </li>
              <li>
                <CheckIcon /> Keep one complete face in frame, with space above the head and below the chin.
              </li>
            </ul>
            <p>
              A phone’s rear camera from a little farther away usually introduces less wide-angle distortion than a very close selfie. The manual calculator is available when a suitable photo is not.
            </p>
            <a className="button button--secondary" href="#tool" onClick={() => changeMode('photo')}>
              Try the photo scan <ArrowIcon />
            </a>
          </div>
        </section>

        <section className="privacy-section section-shell" aria-labelledby="privacy-heading">
          <div className="privacy-section__heading">
            <span className="eyebrow">
              <LockIcon /> Privacy architecture
            </span>
            <h2 id="privacy-heading">The safest upload is no upload.</h2>
          </div>
          <div className="privacy-section__grid">
            <article>
              <span>01</span>
              <h3>Same-origin engine</h3>
              <p>The model and WebAssembly assets are served with the site instead of being fetched from a third-party analyzer after you choose a photo.</p>
            </article>
            <article>
              <span>02</span>
              <h3>Memory-only input</h3>
              <p>The image, landmarks, and result stay in page memory. Resetting, refreshing, or closing the tab removes that temporary state.</p>
            </article>
            <article>
              <span>03</span>
              <h3>Restricted network</h3>
              <p>A Content Security Policy limits runtime connections to this site’s own origin. The launch build contains no ad pixels, remote fonts, or behavioral analytics.</p>
            </article>
            <article>
              <span>04</span>
              <h3>Local export</h3>
              <p>The optional report is drawn into a browser Canvas and downloaded directly. Creating it does not require an account or a server-side rendering request.</p>
            </article>
          </div>
          <button className="text-button privacy-section__policy" type="button" onClick={() => setLegalOpen(true)}>
            Open the full Terms & Privacy policy <ArrowIcon />
          </button>
        </section>

        <section className="questions-section section-shell" aria-labelledby="questions-heading">
          <div className="section-intro section-intro--split">
            <div>
              <span className="eyebrow">Before you scan</span>
              <h2 id="questions-heading">Useful limits, answered plainly.</h2>
            </div>
            <p>
              A good utility should explain where the answer comes from and where it can fail. These are the questions that matter most before using a face-shape label for styling.
            </p>
          </div>

          <div className="question-list">
            {QUESTIONS.map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>
                  <span>0{index + 1}</span>
                  <strong>{item.question}</strong>
                  <span className="question-list__marker" aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="closing-section section-shell" aria-labelledby="closing-heading">
          <div>
            <span className="eyebrow">One task, answered now</span>
            <h2 id="closing-heading">Measure the shape. Keep the photo.</h2>
          </div>
          <div>
            <p>Use the photo scan for speed or four manual measurements for a second opinion. Both routes are free and require no account.</p>
            <a className="button button--primary" href="#tool">
              Find my face shape <ArrowIcon />
            </a>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="site-footer__brand">
          <img src="/favicon.svg" width="48" height="48" alt="" />
          <p>
            <strong>What’s My Face Shape</strong>
            <span>A private, explainable styling utility by DIFFT LLC.</span>
          </p>
        </div>
        <div className="site-footer__links">
          <a href="#tool">Analyzer</a>
          <a href="#shapes">Face shapes</a>
          <a href="#method">Method</a>
          <button type="button" onClick={() => setLegalOpen(true)}>
            Terms & Privacy
          </button>
        </div>
        <div className="site-footer__note">
          <InfoIcon />
          <p>Face-shape categories are styling heuristics, not medical, identity, or attractiveness determinations.</p>
        </div>
        <small>© 2026 DIFFT LLC. Built for private, task-first use.</small>
      </footer>

      <LegalDialog open={legalOpen} onClose={() => setLegalOpen(false)} />
    </>
  );
}

export default App;
